import { useQueryClient } from '@tanstack/react-query';
import { KNOWLEDGE_BASES_QUERY_KEYS } from '@/entities/knowledge-base';
import {
  KnowledgeBaseFormData,
  useKnowledgeBaseActions,
  useKnowledgeBaseDetail,
  useKnowledgeBaseForm,
} from '@/features/knowledge-bases';
import { useKnowledgeBaseModal } from '@/features/knowledge-bases/model/knowledge-base-modal.store';
import { KnowledgeBaseDeleteForm } from './KnowledgeBaseDeleteForm/KnowledgeBaseDeleteForm';
import { KnowledgeBaseForm } from './KnowledgeBaseForm/KnowledgeBaseForm';
import { KnowledgeBaseModalProps } from './KnowledgeBaseModal.types';

export const KnowledgeBaseModal = ({ renderForm = true }: KnowledgeBaseModalProps = {}) => {
  const {
    closeKnowledgeBaseModal,
    isKnowledgeBaseModalOpen,
    knowledgeBaseModalTypeDelete,
    knowledgeBaseModalTypeEdit,
    knowledgeBaseModalType,
  } = useKnowledgeBaseModal();

  // Get knowledge base data for edit mode
  const { knowledgeBase } = useKnowledgeBaseDetail();

  // This surface has no workspace picker, so it keeps the one-workspace reading of a knowledge base
  // whatever the sharing flag says: seeding `workspaces` here would show no option selected, and
  // saving the choice it does offer would drop the other workspaces.
  const { handleConfirmDelete, handleDelete, isSubmitting, handleSubmit } =
    useKnowledgeBaseActions();

  const { formData, setFormData, isFormValid } = useKnowledgeBaseForm(
    knowledgeBaseModalType,
    knowledgeBase,
  );

  const queryClient = useQueryClient();

  // Enhanced submit handler that invalidates queries on success
  const handleSubmitWithInvalidation = async (data: KnowledgeBaseFormData) => {
    await handleSubmit(data, (knowledgeBaseId: string) => {
      void queryClient.invalidateQueries({ queryKey: KNOWLEDGE_BASES_QUERY_KEYS.all });
      void queryClient.invalidateQueries({
        queryKey: KNOWLEDGE_BASES_QUERY_KEYS.detail(knowledgeBaseId),
      });
    });
  };

  if (!knowledgeBaseModalType) return null;

  if ((knowledgeBaseModalTypeEdit || knowledgeBaseModalType === 'create') && !renderForm) {
    return null;
  }

  if (knowledgeBaseModalTypeDelete) {
    return (
      <KnowledgeBaseDeleteForm
        open={isKnowledgeBaseModalOpen}
        isDeleting={isSubmitting}
        onCancel={closeKnowledgeBaseModal}
        onConfirmDelete={() => void handleConfirmDelete(formData)}
        knowledgeBaseName={formData.title}
      />
    );
  }

  return (
    <KnowledgeBaseForm
      data={formData}
      modalType={knowledgeBaseModalType as 'create' | 'edit'}
      setData={setFormData}
      onSubmit={handleSubmitWithInvalidation}
      onCancel={closeKnowledgeBaseModal}
      onDelete={knowledgeBaseModalTypeEdit ? handleDelete : undefined}
      isSubmitting={isSubmitting}
      isFormValid={isFormValid}
    />
  );
};

KnowledgeBaseModal.displayName = 'KnowledgeBaseModal';
